import React, { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { isHexColour } from 'fym-shared/src/music24/kitPalette';
import { TypedMemo } from '../../../../common/types';
import { themeMessages } from './themeMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Theme panel; each part uses its own slice of it */
import styles from './ThemePanel.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ColourSwatchFieldProps {
  /** The place of the colour in the palette, from 0. */
  index: number;
  /** A `#rrggbb` colour. */
  value: string;
  disabled: boolean;
  /** Called with a valid `#rrggbb` colour only. */
  onCommit: (index: number, colour: string) => void;
}

/** The text of a hex field to a colour: it takes a missing `#` and any case. Null when it is not a colour. */
export function colourFromHexText(text: string): string | null {
  const trimmed = text.trim();
  const colour = trimmed.startsWith('#') ? trimmed : `#${trimmed}`;
  return isHexColour(colour) ? colour.toLowerCase() : null;
}

/**
 * One colour of the palette: a colour picker and a hex field (owner request 2026-10-06: "the theme
 * colours should be editable"). The picker changes the draft at once. The hex field keeps what the
 * user types and changes the draft when the text is a full `#rrggbb` colour; a half-typed value
 * stays in the field and is marked invalid. When the field loses focus with a bad value, it shows
 * the colour of the draft again.
 */
const ColourSwatchFieldComponent: React.FC<ColourSwatchFieldProps> = ({ index, value, disabled, onCommit }) => {
  const intl = useIntl();
  const [text, setText] = useState(value);
  const isInvalid = colourFromHexText(text) === null;

  useEffect(() => {
    setText(value);
  }, [value]);

  const onPick = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => onCommit(index, event.target.value.toLowerCase()),
    [index, onCommit]
  );
  const onType = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setText(event.target.value);
      const colour = colourFromHexText(event.target.value);
      if (colour !== null) {
        onCommit(index, colour);
      }
    },
    [index, onCommit]
  );
  const onBlur = useCallback(() => setText(value), [value]);

  const number = index + 1;
  return (
    <span className={styles.swatch} data-testid={`theme-colour-${index}`}>
      <input
        type="color"
        className={styles.swatchPicker}
        value={value}
        disabled={disabled}
        aria-label={intl.formatMessage(themeMessages.coloursSwatch, { number })}
        onChange={onPick}
      />
      <input
        type="text"
        className={styles.swatchHex}
        value={text}
        maxLength={7}
        spellCheck={false}
        autoComplete="off"
        disabled={disabled}
        aria-invalid={isInvalid}
        aria-label={intl.formatMessage(themeMessages.coloursHex, { number })}
        onChange={onType}
        onBlur={onBlur}
      />
    </span>
  );
};

export const ColourSwatchField = TypedMemo(ColourSwatchFieldComponent);
